import {
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput, MonthPickerInput } from '@mantine/dates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { useBusinessDate, useIslandToday } from '../hooks/useSeedLocation';
import { formatDate, formatDateTime, formatMoney } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';

const FILED_PATH = '/admin/v1/accounting/filed-months';

interface FiledMonth {
  month: string;
  monthLabel: string;
  filedOn: string;
  note: string | null;
  filedAt: string;
  filedByName: string | null;
  reopenedAt: string | null;
  reopenedByName: string | null;
  reopenedReason: string | null;
  amendments: number;
}

interface Amendment {
  at: string;
  by: string | null;
  what: string;
  reason: string;
  /** Signed — negative on a credit note — and null on an entry that moves no money, like a punch. */
  amountMinor: number | null;
  currency: string | null;
}

/**
 * Which months the accountant has already filed, and everything that has been written into them
 * since.
 *
 * The screen exists because the mechanism behind it is deliberately not a lock. Nothing here stops
 * anybody doing anything: marking a month filed only makes the office WARN, once, before an entry
 * is dated into that month, and then keeps the list on this page so the accountant has somewhere to
 * look. A system that refused would be one the owner has to work around with a piece of paper he is
 * legally obliged to record.
 */
export function AccountingPage() {
  const [filing, setFiling] = useState(false);
  const [reopening, setReopening] = useState<FiledMonth | null>(null);
  const [showing, setShowing] = useState<FiledMonth | null>(null);

  const months = useQuery({
    queryKey: listKey(FILED_PATH),
    queryFn: () => rawApi<FiledMonth[]>('GET', FILED_PATH),
  });

  const rows = months.data ?? [];

  return (
    <Stack gap="md">
      <div>
        <Title order={3}>Filed months</Title>
        <Text size="sm" c="dimmed" mt={4}>
          Tell the office which months your accountant has already sent in. Nothing is locked — you will
          simply be warned before anything is dated into one of them, and told to say why, so your accountant
          can see what moved after the fact.
        </Text>
      </div>

      <Paper withBorder p="md" radius="md">
        <Group justify="space-between" mb="sm" wrap="nowrap" align="flex-start">
          <div>
            <Group gap="xs">
              <Title order={5}>Months already filed</Title>
              {months.isFetching && !months.isLoading && <Loader size={14} />}
            </Group>
            <Text size="sm" c="dimmed" mt={2}>
              Newest first. A month you have taken back off the list is still shown, with the reason.
            </Text>
          </div>
          <Button size="sm" onClick={() => setFiling(true)}>
            Mark a month as filed
          </Button>
        </Group>

        {months.isError ? (
          <Alert color="red" title="Could not load this list">
            <Text size="sm" mb="sm">
              The information could not be reached. Nothing has been lost — this is only a display problem.
            </Text>
            <Button size="xs" variant="light" onClick={() => void months.refetch()}>
              Try again
            </Button>
          </Alert>
        ) : (
          <ScrollArea type="auto" offsetScrollbars>
            <DataTable
              idAccessor="filedAt"
              records={rows}
              fetching={months.isLoading}
              minHeight={rows.length === 0 ? 140 : undefined}
              noRecordsText="No months filed yet"
              highlightOnHover
              verticalSpacing="sm"
              striped
              styles={{ table: { minWidth: 720 } }}
              columns={[
                {
                  accessor: 'monthLabel',
                  title: 'Month',
                  noWrap: true,
                  render: (m: FiledMonth) => (
                    <Group gap="xs" wrap="nowrap">
                      <Text fw={600}>{m.monthLabel}</Text>
                      {m.reopenedAt && (
                        <Badge color="gray" variant="light">
                          Taken back off the list
                        </Badge>
                      )}
                    </Group>
                  ),
                },
                {
                  accessor: 'filedOn',
                  title: 'Filed',
                  noWrap: true,
                  render: (m: FiledMonth) => (
                    <Stack gap={0}>
                      <Text size="sm">{formatDate(m.filedOn)}</Text>
                      {m.filedByName && (
                        <Text size="xs" c="dimmed">
                          Recorded by {m.filedByName}
                        </Text>
                      )}
                    </Stack>
                  ),
                },
                {
                  accessor: 'note',
                  title: 'Note',
                  render: (m: FiledMonth) =>
                    m.reopenedAt ? (
                      <Text size="xs" c="dimmed">
                        Taken back off on {formatDate(m.reopenedAt)} — {m.reopenedReason}
                      </Text>
                    ) : (
                      <Text size="xs">{m.note ?? ''}</Text>
                    ),
                },
                {
                  accessor: 'amendments',
                  title: 'Entered since',
                  textAlign: 'right',
                  noWrap: true,
                  render: (m: FiledMonth) =>
                    m.reopenedAt ? null : m.amendments === 0 ? (
                      <Text size="sm" c="dimmed">
                        Nothing
                      </Text>
                    ) : (
                      <Button size="xs" variant="light" color="yellow" onClick={() => setShowing(m)}>
                        {m.amendments === 1 ? '1 entry' : `${m.amendments} entries`}
                      </Button>
                    ),
                },
                {
                  accessor: 'filedAt',
                  title: '',
                  textAlign: 'right',
                  noWrap: true,
                  render: (m: FiledMonth) =>
                    m.reopenedAt ? null : (
                      <Button size="xs" variant="subtle" onClick={() => setReopening(m)}>
                        Take it back off
                      </Button>
                    ),
                },
              ]}
            />
          </ScrollArea>
        )}
      </Paper>

      {filing && <FileMonthModal onClose={() => setFiling(false)} />}
      {reopening && <ReopenModal month={reopening} onClose={() => setReopening(null)} />}
      {showing && <AmendmentsModal month={showing} onClose={() => setShowing(null)} />}
    </Stack>
  );
}

/**
 * The day the accountant filed is asked for separately from the month, and defaults to today only
 * because that is the common case — the owner usually records this the same week. It is not the day
 * this screen was opened, and getting the two confused would put the wrong date in front of an
 * auditor.
 */
function FileMonthModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [month, setMonth] = useState<string | null>(null);
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const islandToday = useIslandToday();
  const [filedOn, setFiledOn] = useState<string | null>(islandToday);
  const [note, setNote] = useState('');

  /*
   * The last month that could possibly have been filed: the one before the month the bar is in.
   *
   * The picker used to offer December in July and the server took it, and a filed month warns on
   * every entry dated inside it — so one mis-click put that warning in front of every ordinary
   * entry for the rest of the year, which is how somebody learns to click through a warning.
   *
   * Off the bar's own business day rather than the browser's clock, so this agrees with the server
   * on the one night it could disagree: at half past one on the first of August the bar is still
   * working the night of 31 July, July is not over, and the last filable month is June.
   */
  const businessDate = useBusinessDate();
  const maxMonth = businessDate
    ? // The month before the one the business day falls in. `- 2` because the month read off the
      // date is 1-based and a JavaScript month index is not; a negative index rolls the year back,
      // which is what January needs.
      new Date(Date.UTC(Number(businessDate.slice(0, 4)), Number(businessDate.slice(5, 7)) - 2, 1))
        .toISOString()
        .slice(0, 10)
    : undefined;

  const save = useMutation({
    mutationFn: () =>
      rawApi(
        'POST',
        FILED_PATH,
        // The picker hands back the first of the month; the API wants the month itself.
        { month: (month ?? '').slice(0, 7), filedOn, note: note.trim() || null },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(FILED_PATH) });
      onClose();
      notifyDone('Marked as filed', 'You will be warned before anything is dated into that month.');
    },
    onError: (err) => notifyError(err, 'mark that month as filed'),
  });

  return (
    <Modal opened onClose={onClose} title="Mark a month as filed" size="lg" closeOnClickOutside={false}>
      <Stack gap="md">
        <MonthPickerInput
          label="Which month"
          description="The month the return covers — it has to be a month that has ended"
          placeholder="Pick a month"
          value={month}
          onChange={setMonth}
          valueFormat="MMMM YYYY"
          maxDate={maxMonth}
          popoverProps={{ withinPortal: true }}
        />
        <DateInput
          label="Filed on"
          description="The day your accountant actually sent it, which may not be today"
          value={filedOn}
          onChange={setFiledOn}
          valueFormat="D MMM YYYY"
          popoverProps={{ withinPortal: true }}
        />
        <TextInput
          label="Note"
          description="Optional — anything you want to remember about this filing"
          value={note}
          onChange={(e) => setNote(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={!month || !filedOn} onClick={() => save.mutate()}>
            Mark as filed
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function ReopenModal({ month, onClose }: { month: FiledMonth; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const tooShort = reason.trim().length < 3;

  const save = useMutation({
    mutationFn: () => rawApi('POST', `${FILED_PATH}/${month.month}/reopen`, { reason: reason.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: listKey(FILED_PATH) });
      onClose();
      notifyDone(`${month.monthLabel} is open again`, 'Nothing will be flagged in that month now.');
    },
    onError: (err) => notifyError(err, 'take that month back off the list'),
  });

  return (
    <Modal opened onClose={onClose} title={`Take ${month.monthLabel} back off the list`} size="lg">
      <Stack gap="md">
        <Text size="sm">
          Use this when it turns out the month was not filed after all, or your accountant is going to send it
          again. The record that it was marked filed is kept either way.
        </Text>
        <Textarea
          label="Why"
          placeholder="The accountant had not actually sent it"
          autosize
          minRows={2}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} disabled={tooShort} onClick={() => save.mutate()}>
            Take it back off
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/**
 * The list the accountant is actually handed.
 *
 * Every row says what was recorded and why the person recording it went ahead, in the words they
 * typed. What it deliberately does not show is the machine's own vocabulary — the action name and
 * the record's identifier — because the one person who has to act on this list can do nothing with
 * either.
 */
function AmendmentsModal({ month, onClose }: { month: FiledMonth; onClose: () => void }) {
  const list = useQuery({
    queryKey: listKey(`${FILED_PATH}/${month.month}/amendments`),
    queryFn: () => rawApi<Amendment[]>('GET', `${FILED_PATH}/${month.month}/amendments`),
  });

  return (
    <Modal opened onClose={onClose} title={`Entered into ${month.monthLabel} after it was filed`} size="xl">
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Your accountant decides what to do with these — carry them forward, or amend the return. Everything
          below is dated inside {month.monthLabel} and was recorded after {formatDate(month.filedOn)}.
        </Text>
        <ScrollArea type="auto" offsetScrollbars>
          <DataTable
            idAccessor="at"
            records={list.data ?? []}
            fetching={list.isLoading}
            minHeight={(list.data ?? []).length === 0 ? 120 : undefined}
            noRecordsText="Nothing has been entered into this month since it was filed"
            verticalSpacing="sm"
            striped
            styles={{ table: { minWidth: 640 } }}
            columns={[
              {
                accessor: 'what',
                title: 'What was recorded',
                render: (a: Amendment) => <Text size="sm">{a.what}</Text>,
              },
              {
                accessor: 'amountMinor',
                title: 'How much',
                textAlign: 'right',
                noWrap: true,
                // The column this list cannot do its job without. Whether a month is worth amending
                // is a judgement about how much moved, and every row here used to read the same
                // handful of words with no figure anywhere — so the one person the screen exists
                // for had to open each entry elsewhere to find out whether it mattered.
                /*
                 * An absence, not a zero, and deliberately not the words "no money".
                 *
                 * A corrected punch has no figure to state and still costs the month something —
                 * it changes what that month paid in wages, by an amount this entry cannot know.
                 * Printing "No money" beside it would be a claim, and the wrong one, on a list
                 * whose whole purpose is telling an accountant what moved.
                 */
                render: (a: Amendment) =>
                  a.amountMinor === null || !a.currency ? (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  ) : (
                    <Text size="sm" fw={600} c={a.amountMinor < 0 ? 'teal.7' : undefined}>
                      {formatMoney(a.amountMinor, a.currency)}
                    </Text>
                  ),
              },
              {
                accessor: 'reason',
                title: 'Why it was recorded anyway',
                render: (a: Amendment) => <Text size="sm">{a.reason}</Text>,
              },
              {
                accessor: 'at',
                title: 'Recorded',
                noWrap: true,
                render: (a: Amendment) => (
                  <Stack gap={0}>
                    <Text size="sm">{formatDateTime(a.at)}</Text>
                    {a.by && (
                      <Text size="xs" c="dimmed">
                        {a.by}
                      </Text>
                    )}
                  </Stack>
                ),
              },
            ]}
          />
        </ScrollArea>
      </Stack>
    </Modal>
  );
}
